<template>
  <div class="svg-renderer">
    <div ref="svgModel" class="svg-renderer-model">
      <div v-html="svgData" ref="svgRendererWrapper" class="svg-renderer-wrapper"></div>
      <div :style="style('render-passes.png')" class="svg-renderer-render"></div>
      <div v-if="!isApparel" :style="style('top-shadow.png')" class="svg-renderer-shadow"></div>
    </div>
    <template v-for="(item, key) in images">
      <canvas :ref="`${key}SvgCanvas`" class="svg-renderer-canvas" width="1024" height="1024" :key="key"></canvas>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import http from '@/http';
import smartcrop from 'smartcrop';
import canvasUtils from '@/plugins/canvas/CanvasUtils';
import CustomsSvgServices from '@vfdp-customs/customs-svg-services';
import FormatRecipeForSVGService from "@/libs/graphics/FormatRecipeForSVGService";
import RecipeCreateFromGDM from "@/libs/graphics/RecipeCreateFromGDM";
import {GraphicPositionData} from "@/libs/graphics/GraphicsHelper";

export default Vue.extend({
  name: 'SvgRenderer',
  props: {
    /*
     * @property - enableSmartcrop
     * Sets if smartcrop should be used or not
     */
    enableSmartcrop: {
      type: Boolean,
      default: false
    },
    /*
     * @property - imageRatio
     * Sets image ratio to use for the svg component
     */
    imageRatio: {
      type: Function,
      default: () => 1
    },
    /*
     * @property - images
     * Sets image url per component to use for the svg component, null will show default model, empty object means is waiting
     */
    images: {
      type: Object,
      default: null
    },
    /*
     * @property - package
     * Sets package that is using the svg component
     */
    package: {
      type: String,
      default: 'ugcentrypoint'
    },
    /*
     * @property - model
     * Sets model to use for the svg component
     */
    model: {
      type: String,
      required: true
    },
    /*
     * @property - view
     * Sets view to use for the svg component
     */
    view: {
      type: String,
      required: true
    },
    /*
     * @property - isSvgSelected
     * Indicates that the svg has been selected and it needs to emit the canvas image url to the parent.
     */
    isSvgSelected: {
      type: Boolean,
      default: false
    },
    colorMapping: {
      type: Object
    },
    productType: {
      type: String,
      default: "shoe"
    },
    graphicId: {
      type: String,
      default: null
    },
    svgSize: {
      type: Object,
      default: null
    },
    svgImagePlacement: {
      type: Object,
      default: null
    },
    componentName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      croppedImages: [],
      croppedPlacement: [],
      jsonData: null,
      svgData: null,
      isLoading: true,
      placementImageLoaded: 0,
      svgFinishedLoading: false,
      placementCropSettings: {},
      svgModel: null,
      customsSvgService: null
    };
  },
  computed: {
    /*
     * @computed - url2dRenderer
     * Defines baseUrl to use so svg renderer can request all assets needed
     */
    url2dRenderer() {
       // TODO: integrate url2dRenderer with Randomizer
      return `${ this.$store.state.endPoints[this.$props.package].customsAssets }/svg/2d-renderer/${ this.$props.model }/${ this.$props.view }/`;
    },
    /*
     * @computed - style
     * Returns background image style depending on the file name sended as an argument
     */
    style() {
      return file => ({ backgroundImage: `url(${ this.url2dRenderer }assets/${ file })` });
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.$store.state.utils.configuration.enablePerformanceLogging;
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    uploadedImageUrl() {
      return this.$store.state.UGCEntryPoint.uploadedImageUrl;
    },
    componentsWithGraphicsSupport() {
      return ["graphic_front"];
    },
    graphicPlacementSetting() {
      return this.$store.state.graphicsController.placementSetting.find(placement => placement.componentSetting.id = "fill_ugc");
    },
    isApparel() {
      return this.productType === "apparel"
    }
  },
  watch: {
    /*
     * @watch - images
     * Calls cropImage when images changes and has elements
     * add view validation to avoid execute the cropImage more times when is the first view for style selection
     */
    ['$props.images'](val) {
      const {activeView , views } = this.$store.state.UGCEntryPoint;
      if (val && Object.keys(val).length && this.svgFinishedLoading) {
        this.cropImage();
      }
    },
    ['$props.isSvgSelected'](val) {
      if (val) {
        this.sendSvgSelected();
      }
    },
    svgFinishedLoading(val) {
      if(val && (this.$props.images && Object.keys(this.$props.images).length > 0)) {
        this.cropImage();
      }
    },
    isLoading(val) {
      if (this.$props.images === null) {
        this.$nextTick(() => this.$emit('isLoading', this.isLoading));
      } else {
        if (this.placementImageLoaded === Object.keys(this.$props.images).length) {
          this.$nextTick(() => this.$emit('isLoading', false));
        }
      }
    },
  },
  methods: {
    /*
     * @method - getData
     * Request asset with fileName passed, and assign it into dataAttribute of vue component data
     * @param {string} fileName - Name of the file to get
     * @param {string} dataAttribute - Name of vue component data attribute to store response
     */
    getData(fileName, dataAttribute) {
      if (fileName === "data-square.json" && this.productType==="apparel" && this.graphicId) {
        return Promise.resolve();
      } else {
        return http
          .get(this.url2dRenderer + fileName)
          .then(({ data }) => {
            // The file data-square.json contains the placement crop settings used
            // to get the placement using GET_CROPPED_UGC_PLACEMENT endpoint
            if (fileName === "data-square.json") {
              this[dataAttribute] = data["svg-settings"];
              this.placementCropSettings = data["placement-crop-settings"];
            } else {
              this[dataAttribute] = data;
            }
          })
          .catch(err => console.error(`SVGRenderer - getData: error loading ${ fileName }`, err));
      }
    },
    /*
     * @method - setSvgAttributes
     * Sets initial svg attributes to svg DOM element
     */
    setSvgAttributes() {
      if (!this.jsonData || !this.svgData) {
        console.error('SVGRenderer - setSvgAttributes: error jsonData or svgData not properly defined');
        return false;
      }

      Object.keys(this.jsonData).forEach(name => {
        const patternEl = this.$refs['svgRendererWrapper'].querySelector(`pattern#${ name }`);

        if (patternEl) {
          patternEl.setAttribute('x', this.jsonData[name].x);
          patternEl.setAttribute('y', this.jsonData[name].y);
          patternEl.setAttribute('width', this.jsonData[name].width);
          patternEl.setAttribute('height', this.jsonData[name].height);
          patternEl.setAttribute('patternTransform', this.jsonData[name].patternTransform);

          const imageEl = patternEl.querySelector('image');

          if (imageEl && this.jsonData[name].image) {
            imageEl.setAttribute('x', this.jsonData[name].image.x);
            imageEl.setAttribute('y', this.jsonData[name].image.y);
            imageEl.setAttribute('width', this.jsonData[name].image.width);
            imageEl.setAttribute('height', this.jsonData[name].image.height);
          }
        }
      });

      return true;
    },
    /*
     * @method - cropImage
     * Makes crop image process
     */
    cropImage() {
      this.isLoading = true;
      Object.keys(this.$props.images).forEach(component => {
        const croppedImage = new Image();
        croppedImage.setAttribute('crossorigin', 'anonymous');
        croppedImage.src = this.$props.images[component];
        croppedImage.onload = () => {
          const { height, width } = croppedImage;
          if(this.$props.enableSmartcrop) { // If smartCrop is enabled, use it
            const currentImageRatio = height / width;
            const imageRatio = this.$props.imageRatio(component);
            let cropWidth, cropHeight;

            if (imageRatio > currentImageRatio) {
              cropHeight = height;
              cropWidth = height / imageRatio;
            } else if (imageRatio <= currentImageRatio) {
              cropWidth = width;
              cropHeight = width * imageRatio;
            }

            smartcrop.crop(croppedImage, { width: cropWidth, height: cropHeight }).then(({ topCrop }) => {
              if (this.$store.state.utils.configuration.enableCropPlacement) {
                this.getCroppedPlacement(component, topCrop.x, topCrop.y, topCrop.width, topCrop.height);
              } else {
                this.setSvgImage(croppedImage, component, topCrop.x, topCrop.y, topCrop.width, topCrop.height); // Set svg image with values from smartcrop
              }
              this.croppedPlacement[component] = {
                original_image: this.$props.images[component],
                x: topCrop.x,
                y: topCrop.y,
                width: topCrop.width,
                height: topCrop.height
              };
            });
          } else { // If smartCrop is disabled
            if (this.configuration.enableCropPlacement) {
              this.getCroppedPlacement(component, 0, 0, width, height);
            } else {
              if (this.configuration.enableUseSquareImage) {
                // If the image is base64 use canvas. Otherwise, it uses the url
                this.$props.images[component].startsWith("data:") ? this.setSvgImage(croppedImage, component, 0, 0, width, height) : this.setSvgImageUrl(this.$props.images[component], component);
              } else {
                this.setSvgImage(croppedImage, component, 0, 0, width, height);
              }
            }
            const cropSettings = this.placementCropSettings[component];
            // The cropped placement is from the data-square.json
            if (this.configuration.enableUseSquareImage && cropSettings) {
              const _imageRatio = this.$props.imageRatio();
              this.croppedPlacement[component] = {
                original_image: this.$props.images[component],
                x: cropSettings.x * _imageRatio,
                y: cropSettings.y * _imageRatio,
                width: cropSettings.width * _imageRatio,
                height: cropSettings.height * _imageRatio
              };
            } else {
              this.croppedPlacement[component] = {
                original_image: this.$props.images[component],
                x: 0,
                y: 0,
                width: width,
                height: height
              };
            }
          }
        };
        this.croppedImages.push(croppedImage);
      });
    },
    getCroppedPlacement(component, startx, starty, endx, endy) {
      const canvas = this.$refs[`${component}SvgCanvas`][0];
      canvas.width = endx;
      canvas.height = endy;

      // This logic is only valid for ugcentrypoint
      const croppedPlacement = {
        original_image: this.$props.images[component],
        x: startx,
        y: starty,
        width: endx,
        height: endy
      }
      this.$store.dispatch('UGCEntryPoint/GET_CROPPED_UGC_PLACEMENT', { croppedPlacement }).then(({ data }) => {
        if(this.enablePerformanceLogging)
          console.log("Cropped UGC placement Ready: ", new Date().getTime() / 1000 - this.initialTime);

        const imageUrl = data[0].image_url;
        let newImage = new Image();
        newImage.crossOrigin = "anonymous";
        newImage.onload = async() => {
          canvas.getContext('2d').drawImage(
            newImage, // Image to crop
            0, 0
          );
          const newImgUrl = await canvasUtils.setImageUrlFromCanvas(canvas);
          this.applyImageToSvg(component, newImgUrl);

          this.$nextTick(() => {
            this.placementImageLoaded++;
            this.isLoading = !this.isLoading;
          });
        }
        newImage.src = imageUrl;
      });
    },
    applyImageToSvg(component, image) {
      Object.keys(this.jsonData).forEach(name => {
        if (this.jsonData[name].component === component) {
          const patternEl = this.$refs['svgRendererWrapper'].querySelector(`pattern#${ name }`);
          const pathsEl = this.$refs['svgRendererWrapper'].querySelectorAll(`path.${ name }`);

          if (patternEl) {
            const imageEl = patternEl.querySelector('image');

            if (imageEl) {
              imageEl.setAttribute('xlink:href', image);
            }
          }
          if (pathsEl) {
            // To avoid showing the black svg
            patternEl.querySelector("image").onload = () => {
              pathsEl.forEach(pathEl => pathEl.style.fill = `url(#${ name })`);
            }
          }
        }
      });
    },
    async setSvgImageUrl(imageUrl, component) {
      if (!this.jsonData && this.productType !== "apparel") {
        console.error('SVGRenderer - setSvgImage: error jsonData  not properly defined');
        return false;
      }
      if(this.productType==="apparel" && this.graphicId) {
        await this.createSvgModel();
      } else {
        await this.applyImageToSvg(component, imageUrl);
      }
      this.$nextTick(() => {
        if (this.$store.state.UGCEntryPoint.enableAutoColorMapping) {
          this.applyColorMapping();
        }
        this.placementImageLoaded++;
        this.isLoading = !this.isLoading;
      });
    },
    /*
     * @method - setSvgImage
     * Sets image svg attribute to svg DOM element
     */
    async setSvgImage(croppedImage, component, startx, starty, endx, endy) {
      if (!this.jsonData) {
        console.error('SVGRenderer - setSvgImage: error jsonData  not properly defined');
        return false;
      }

      const canvas = this.$refs[`${component}SvgCanvas`][0];
      canvas.width = endx;
      canvas.height = endy;

      canvas.getContext('2d').drawImage(
        croppedImage, // Image to crop
        startx, starty, // Start position to crop
        endx, endy, // End position to crop
        0, 0, // Start position to paint in canvas
        endx, endy // End position to paint in canvas
      );

      const imageUrl = await canvasUtils.setImageUrlFromCanvas(canvas);
      this.applyImageToSvg(component, imageUrl);

      this.$nextTick(() => {
        if (this.$store.state.UGCEntryPoint.enableAutoColorMapping) {
          this.applyColorMapping();
        }
        this.placementImageLoaded++;
        this.isLoading = !this.isLoading;
      });
    },
    async sendSvgSelected() {
      const canvasModel = this.$refs['svgModel'].cloneNode(true);
      Object.keys(this.jsonData).forEach(name => {
        const patternEl = canvasModel.querySelector(`pattern#${ name }`);
        const pathsEl = canvasModel.querySelectorAll(`path.${ name }`);

        if (patternEl) {
          patternEl.setAttribute('id', `${ name }-clone`);
        }

        if (pathsEl) {
          pathsEl.forEach(pathEl => {
            pathEl.classList.remove(name);
            pathEl.classList.add(`${ name }-clone`);
            if (this.images && Object.keys(this.images).length) {
              pathEl.style.fill = `url(#${ name }-clone)`;
            }
          });
        }
      });
      let placementImage = {};
      for (let component in this.$props.images) {
        const canvas = this.$refs[`${component}SvgCanvas`][0];
        const blob = await canvasUtils.getBlobFromCanvas(canvas);
        const imgUrl = await canvasUtils.convertBlobToBase64(blob);
        placementImage[component] = imgUrl;
      }
      this.$emit('svgSelected', {
        canvasModel,
        placementImage,
        croppedPlacement: this.croppedPlacement
      });
    },
    applyColorMapping() {
      Object.keys(this.colorMapping).forEach(component => {
        const elements = this.$refs.svgRendererWrapper.getElementsByClassName(component);
        const swatch = this.$store.state.UGCEntryPoint.colorMappingData.available_swatches.filter(item => item.swatch_id === this.colorMapping[component]);

        for (let componentElement of elements) {
          componentElement.style.fill = swatch[0].color;
        }
      });
    },
    async createSvgModel() {
      const graphic = await this.$store.dispatch('gdm/GET_GDM', { id: this.graphicId, component3D: 'graphic_front' })
      const initialGDMRecipe = RecipeCreateFromGDM(graphic, true)
      const formattedRecipeComponent = await FormatRecipeForSVGService({
        componentID: 'graphic_front',
        graphicInput: graphic,
        recipeComponent: initialGDMRecipe,
      })

      this.svgModel = await this.customsSvgService.buildSvg(formattedRecipeComponent);

      const graphicPlacementData = GraphicPositionData({
        graphic: graphic,
        productID: this.model,
        placementID: 'graphic_front',
      });

      await this.customsSvgService.updateViewport(
        this.svgModel,
        graphicPlacementData.x,
        graphicPlacementData.y,
        graphicPlacementData.width,
        graphicPlacementData.height,
      );

      const targetSVG = this.$refs['svgRendererWrapper'].querySelector('.graphic_front');
      if (targetSVG) targetSVG.innerHTML = this.svgModel.getSvg();
    }
  },
  mounted() {
    const filename = this.configuration.enableUseSquareImage ? 'data-square.json' : 'data.json';
    this.$emit('isLoading', this.isLoading);
    if(this.productType==="apparel") {
      this.customsSvgService = new CustomsSvgServices({
        assetsBucket: this.$store.state.endPoints.ugcentrypoint.customsSvgServiceEndpoint
      });
    }
    this.getData(filename, 'jsonData')
      .then(() => this.getData('model2D.svg', 'svgData'))
      .then((svgData) => this.$nextTick(() => {
        this.$nextTick(() => {
          if(!this.graphicId) {
            this.setSvgAttributes();
          }
          this.svgFinishedLoading = true;
          if(this.enablePerformanceLogging)
            console.log("SVGRenderer 2D image done: ", new Date().getTime() / 1000 - this.initialTime);
        })
      }));
  },
  beforeDestroy() {
    if (this.croppedImages.length) {
      this.croppedImages.forEach(image => {
        image.onload = null;
        image = null;
      });
      this.croppedImages = [];
    }
  }
});
</script>
<style lang="scss">
  .customs-tools {
    @import "~@/styles/_components/SvgRenderer/SvgRenderer.scss";
  }
</style>
